import React, { useState, useEffect } from 'react';
import { Gift, Snowflake, Star, Calendar, DollarSign, Lock, CreditCard, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ChristmasLanding = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    hearAbout: '',
    serviceInterest: '',
    offerInterest: '',
    comments: ''
  });

  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Calculate time remaining until December 31, 2025
  useEffect(() => {
    const calculateTimeLeft = () => {
      const endDate = new Date('2025-12-31T23:59:59');
      const now = new Date();
      const difference = endDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const offers = [
    {
      id: 1,
      icon: Gift,
      title: 'Buy 7+ Windows',
      subtitle: 'Get 1 Window FREE',
      description: 'Upgrade your home with energy-efficient windows and receive one additional window at no cost!',
      color: 'from-red-600 to-red-700'
    },
    {
      id: 2,
      icon: Home,
      title: 'Buy 15+ Windows',
      subtitle: 'Get 1 Sliding Door FREE',
      description: 'Complete your home transformation and enjoy a complimentary sliding door installation.',
      color: 'from-green-700 to-green-800'
    },
    {
      id: 3,
      icon: Lock,
      title: 'Front Entry Door',
      subtitle: 'FREE Locks Included',
      description: 'Enhance your home security with premium locks at no additional charge.',
      color: 'from-red-700 to-red-800'
    },
    {
      id: 4,
      icon: Home,
      title: 'Installation Savings',
      subtitle: 'Up to 80% OFF',
      description: 'Save big on professional installation! Get up to 80% off installation costs this Christmas season.',
      color: 'from-green-600 to-green-700'
    },
    {
      id: 5,
      icon: DollarSign,
      title: 'Color Upgrade',
      subtitle: 'FREE or We Pay Your Tax',
      description: 'Choose your perfect color at no extra cost, or let us cover your taxes!',
      color: 'from-red-600 to-red-800'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you shortly.');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="christmas-landing">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/95 border-b border-red-100 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_winter-savings/artifacts/oqsvy742_image.png"
              alt="The Window Experts"
              className="h-16 md:h-20 w-auto"
            />
            <div className="hidden sm:block border-l-2 border-red-200 pl-4">
              <p className="text-base md:text-lg text-red-600 font-bold flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Christmas Special Offers
              </p>
            </div>
          </div>
          <a href="tel:4168440099">
            <Button className="bg-red-600 hover:bg-red-700 text-white px-4 md:px-6 py-3 font-bold shadow-lg text-base">
              <span className="hidden sm:inline">📞 </span>(416) 844-0099
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section pt-40 md:pt-40 pb-16 relative overflow-hidden min-h-screen flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-800 to-green-900 opacity-95"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzAgMS4xLS45IDItMiAycy0yLS45LTItMiAuOS0yIDItMiAyIC45IDIgMm0tMTggMGMwIDEuMS0uOSAyLTIgMnMtMi0uOS0yLTIgLjktMiAyLTIgMiAuOSAyIDJtMzYgMGMwIDEuMS0uOSAyLTIgMnMtMi0uOS0yLTIgLjktMiAyLTIgMiAuOSAyIDJNMzYgMzJjMCAxLjEtLjkgMi0yIDJzLTItLjktMi0yIC45LTIgMi0yIDIgLjkgMiAybS0xOCAwYzAgMS4xLS45IDItMiAycy0yLS45LTItMiAuOS0yIDItMiAyIC45IDIgMm0zNiAwYzAgMS4xLS45IDItMiAycy0yLS45LTItMiAuOS0yIDItMiAyIC45IDIgMk0zNiA1MGMwIDEuMS0uOSAyLTIgMnMtMi0uOS0yLTIgLjktMiAyLTIgMiAuOSAyIDJtLTE4IDBjMCAxLjEtLjkgMi0yIDJzLTItLjktMi0yIC45LTIgMi0yIDIgLjkgMiAybTM2IDBjMCAxLjEtLjkgMi0yIDJzLTItLjktMi0yIC45LTIgMi0yIDIgLjkgMiAyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        </div>
        
        {/* Christmas Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          {/* Snowflakes */}
          <Snowflake className="absolute top-10 left-[10%] w-16 h-16 text-white/30 animate-float" />
          <Snowflake className="absolute top-20 right-[15%] w-12 h-12 text-white/20 animate-float-delayed" />
          <Snowflake className="absolute top-[40%] left-[5%] w-10 h-10 text-white/25 animate-float" />
          <Snowflake className="absolute bottom-20 right-[20%] w-14 h-14 text-white/20 animate-float-delayed" />
          <Star className="absolute top-[30%] right-[10%] w-8 h-8 text-yellow-300/40 animate-pulse" />
          <Star className="absolute top-[60%] left-[15%] w-6 h-6 text-yellow-300/30 animate-pulse-slow" />
          
          {/* Christmas Lights Effect */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-500 via-yellow-400 via-green-500 via-blue-500 to-red-500 opacity-60"></div>
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="text-white text-center lg:text-left">
              <div className="inline-flex items-center gap-2 mb-4 md:mb-6 px-4 md:px-6 py-2 md:py-3 bg-white/20 backdrop-blur-md rounded-full font-bold text-sm md:text-base border-2 border-white/30 animate-bounce-gentle">
                <Gift className="w-4 h-4 md:w-5 md:h-5" />
                LIMITED TIME CHRISTMAS OFFERS!
              </div>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl xl:text-8xl font-extrabold mb-4 md:mb-6 leading-tight">
                Transform Your Home This
                <span className="block mt-2 md:mt-3 text-yellow-300 drop-shadow-2xl">
                  Christmas!
                </span>
              </h2>
              
              <p className="text-lg sm:text-xl md:text-2xl mb-2 md:mb-4 text-white/90 font-semibold">
                5 Exclusive Holiday Offers
              </p>
              <p className="text-base md:text-lg mb-6 md:mb-8 text-white/80">
                Premium Energy-Efficient Windows & Doors for Greater Toronto Area
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-10 justify-center lg:justify-start">
                <a href="https://thewindowexperts.ca/christmas-quote/" target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-6 md:px-8 py-5 md:py-7 text-lg md:text-xl font-black rounded-xl md:rounded-2xl shadow-2xl transform hover:scale-105 transition-all border-4 border-yellow-300"
                  >
                    <Gift className="w-5 h-5 md:w-6 md:h-6 inline mr-2" />
                    Get FREE Quote
                  </Button>
                </a>
                <Button 
                  onClick={() => document.getElementById('offers').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="bg-white/10 backdrop-blur-md border-2 border-white text-white hover:bg-white/20 px-6 md:px-8 py-5 md:py-7 text-lg md:text-xl font-bold rounded-xl md:rounded-2xl"
                >
                  <Star className="w-5 h-5 md:w-6 md:h-6 inline mr-2" />
                  View Offers
                </Button>
              </div>
              
              {/* Countdown Timer */}
              <div className="bg-yellow-400 rounded-xl md:rounded-2xl p-4 md:p-6 border-4 border-yellow-300 shadow-2xl max-w-md mx-auto lg:mx-0">
                <div className="text-center mb-3">
                  <p className="text-gray-900 font-black text-base md:text-lg">⏰ OFFERS END IN:</p>
                </div>
                <div className="grid grid-cols-4 gap-1.5 md:gap-2">
                  <div className="bg-gray-900 rounded-lg md:rounded-xl p-2 md:p-3 text-center">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400">{timeLeft.days}</div>
                    <div className="text-[10px] md:text-xs font-bold text-white mt-1">DAYS</div>
                  </div>
                  <div className="bg-gray-900 rounded-lg md:rounded-xl p-2 md:p-3 text-center">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400">{timeLeft.hours}</div>
                    <div className="text-[10px] md:text-xs font-bold text-white mt-1">HOURS</div>
                  </div>
                  <div className="bg-gray-900 rounded-lg md:rounded-xl p-2 md:p-3 text-center">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400">{timeLeft.minutes}</div>
                    <div className="text-[10px] md:text-xs font-bold text-white mt-1">MINS</div>
                  </div>
                  <div className="bg-gray-900 rounded-lg md:rounded-xl p-2 md:p-3 text-center">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400">{timeLeft.seconds}</div>
                    <div className="text-[10px] md:text-xs font-bold text-white mt-1">SECS</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Festive Offers Cards */}
            <div className="relative mt-8 lg:mt-0">
              {/* Christmas Decorations Background */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
                <Snowflake className="absolute top-0 right-10 w-16 h-16 text-white animate-float" />
                <Star className="absolute top-20 left-10 w-12 h-12 text-yellow-300 animate-pulse" />
                <Gift className="absolute bottom-20 right-20 w-14 h-14 text-white animate-bounce-gentle" />
              </div>
              
              {/* Festive Offers Grid */}
              <div className="grid grid-cols-1 gap-4 relative z-10">
                {offers.map((offer, index) => (
                  <div 
                    key={offer.id}
                    className="bg-white/95 backdrop-blur-lg rounded-2xl overflow-hidden shadow-2xl border-4 border-yellow-300 transform hover:scale-105 transition-all duration-300"
                  >
                    {/* Christmas Lights Top */}
                    <div className="h-2 bg-gradient-to-r from-red-500 via-yellow-400 via-green-500 to-red-500"></div>
                    
                    <div className="p-4 md:p-5 flex items-center gap-4">
                      {/* Icon */}
                      <div className={`bg-gradient-to-br ${offer.color} rounded-2xl p-3 md:p-4 relative overflow-hidden flex-shrink-0`}>
                        <Snowflake className="absolute top-1 right-1 w-4 h-4 text-white/30" />
                        <offer.icon className="w-8 h-8 md:w-10 md:h-10 text-white relative z-10" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h4 className="text-sm md:text-base font-bold text-gray-900 mb-1">{offer.title}</h4>
                        <p className="text-lg md:text-2xl font-black bg-gradient-to-r from-red-600 to-green-600 bg-clip-text text-transparent">
                          {offer.subtitle}
                        </p>
                      </div>
                      
                      {/* Star Badge */}
                      <div className="flex-shrink-0">
                        <div className="bg-yellow-400 rounded-full p-2 border-2 border-yellow-500 shadow-lg">
                          <Star className="w-5 h-5 md:w-6 md:h-6 text-gray-900 fill-current" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Christmas Lights Bottom */}
                    <div className="h-2 bg-gradient-to-r from-green-500 via-red-500 via-yellow-400 to-green-500"></div>
                  </div>
                ))}
              </div>
              
              {/* Christmas Decorated Images */}
              <div className="grid grid-cols-2 gap-3 md:gap-4 mt-6">
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-yellow-300 relative group">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_winter-savings/artifacts/0jtebbpi_Christmas%201.jpg"
                    alt="Christmas Decorated House with Lights"
                    className="w-full h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-yellow-300 relative group">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_winter-savings/artifacts/ntf4t3yi_Christmas%202.jpg"
                    alt="Christmas Decorated Door and Windows"
                    className="w-full h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-6 bg-white/20 backdrop-blur-md rounded-2xl p-4 border-2 border-white/30">
                <div className="flex gap-4 justify-center text-center">
                  <div className="flex-1">
                    <div className="text-2xl md:text-3xl font-black text-yellow-300">2,000+</div>
                    <div className="text-xs text-white/90">Customers</div>
                  </div>
                  <div className="flex-1">
                    <div className="text-2xl md:text-3xl font-black text-yellow-300">10,000+</div>
                    <div className="text-xs text-white/90">Installations</div>
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-1 justify-center mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-300 text-yellow-300" />
                      ))}
                    </div>
                    <div className="text-xs text-white/90">100+ Reviews</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section id="offers" className="py-20 bg-gradient-to-b from-red-900 via-green-900 to-red-900 relative overflow-hidden">
        {/* Festive Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Snowflakes */}
          <Snowflake className="absolute top-10 left-[5%] w-20 h-20 text-white/10 animate-float" />
          <Snowflake className="absolute top-[20%] right-[8%] w-16 h-16 text-white/10 animate-float-delayed" />
          <Snowflake className="absolute top-[60%] left-[10%] w-12 h-12 text-white/10 animate-float" />
          <Snowflake className="absolute bottom-20 right-[15%] w-24 h-24 text-white/10 animate-float-delayed" />
          
          {/* Stars */}
          <Star className="absolute top-[15%] left-[20%] w-10 h-10 text-yellow-300/20 animate-pulse-slow" />
          <Star className="absolute top-[45%] right-[25%] w-8 h-8 text-yellow-300/20 animate-pulse" />
          <Star className="absolute bottom-[30%] left-[15%] w-12 h-12 text-yellow-300/20 animate-pulse-slow" />
          
          {/* Christmas Ornaments Effect */}
          <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-r from-red-500 via-yellow-400 via-green-500 via-red-500 to-yellow-400 opacity-30"></div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-r from-green-500 via-red-500 via-yellow-400 via-green-500 to-red-500 opacity-30"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            {/* Festive Badge */}
            <div className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 px-8 py-4 rounded-full font-black mb-6 text-lg shadow-2xl border-4 border-yellow-300 animate-bounce-gentle">
              <Gift className="w-6 h-6" />
              🎄 EXCLUSIVE CHRISTMAS DEALS 🎅
            </div>
            
            {/* Title with Christmas Style */}
            <h3 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 drop-shadow-2xl">
              <span className="block">5 Amazing</span>
              <span className="block text-yellow-300 mt-2">Holiday Offers</span>
            </h3>
            
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-semibold">
              🎁 Choose the perfect deal for your home renovation project 🎁
            </p>
            
            {/* Christmas Decoration */}
            <div className="flex justify-center items-center gap-4 mt-6">
              <div className="w-16 h-1 bg-yellow-400 rounded-full"></div>
              <Star className="w-8 h-8 text-yellow-300 fill-yellow-300" />
              <div className="w-16 h-1 bg-yellow-400 rounded-full"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto items-stretch">
            {offers.map((offer, index) => (
              <Card 
                key={offer.id}
                className="offer-card group hover:shadow-2xl transition-all duration-300 border-4 border-yellow-300 overflow-hidden relative transform hover:-translate-y-2 flex flex-col h-full"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-0 relative flex flex-col h-full">
                  {/* Christmas Ribbon */}
                  <div className="absolute top-0 right-0 z-20">
                    <div className="bg-yellow-400 text-gray-900 px-6 py-3 font-black text-sm rounded-bl-3xl shadow-2xl border-l-4 border-b-4 border-yellow-500">
                      🎁 HOT DEAL
                    </div>
                  </div>
                  
                  {/* Festive Top Border */}
                  <div className="h-3 bg-gradient-to-r from-red-500 via-yellow-400 via-green-500 to-red-500"></div>
                  
                  <div className={`bg-gradient-to-br ${offer.color} p-8 text-white relative overflow-hidden min-h-[240px] flex flex-col justify-center`}>
                    {/* Animated Background Elements */}
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="absolute top-1/2 left-1/2 w-20 h-20 bg-white/5 rounded-full -ml-10 -mt-10"></div>
                    
                    {/* Christmas Decorations */}
                    <Snowflake className="absolute top-4 left-4 w-8 h-8 text-white/30 animate-float" />
                    <Snowflake className="absolute bottom-4 right-8 w-6 h-6 text-white/25 animate-float-delayed" />
                    <Star className="absolute top-8 right-4 w-7 h-7 text-yellow-300/40 animate-pulse" />
                    <Star className="absolute bottom-8 left-8 w-5 h-5 text-yellow-300/30 animate-pulse-slow" />
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="bg-white/25 backdrop-blur-sm rounded-3xl p-4 inline-block mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all shadow-xl border-2 border-white/30">
                        <offer.icon className="w-12 h-12" />
                      </div>
                      <h4 className="text-2xl md:text-3xl font-black mb-2 drop-shadow-2xl">{offer.title}</h4>
                      <p className="text-3xl md:text-4xl font-black drop-shadow-2xl leading-tight">{offer.subtitle}</p>
                    </div>
                  </div>
                  
                  {/* Card Body with Festive Styling */}
                  <div className="p-6 bg-gradient-to-b from-white to-red-50 flex-1 flex flex-col">
                    <p className="text-gray-800 leading-relaxed font-semibold mb-4 flex-1">{offer.description}</p>
                    <a href="https://thewindowexperts.ca/christmas-quote/" target="_blank" rel="noopener noreferrer" className="block">
                      <Button 
                        className="w-full bg-gradient-to-r from-red-600 to-green-600 hover:from-red-700 hover:to-green-700 text-white font-black py-4 text-lg rounded-xl shadow-xl transform hover:scale-105 transition-all border-2 border-yellow-400"
                      >
                        <Gift className="w-5 h-5 inline mr-2" />
                        Claim This Offer
                      </Button>
                    </a>
                  </div>
                  
                  {/* Festive Bottom Border */}
                  <div className="h-3 bg-gradient-to-r from-green-500 via-red-500 via-yellow-400 to-green-500"></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Urgency Banner */}
          <div className="mt-16 max-w-6xl mx-auto">
            <div className="relative bg-gradient-to-r from-red-700 via-green-700 to-red-700 rounded-3xl p-10 md:p-12 text-center text-white shadow-2xl overflow-hidden border-8 border-yellow-400">
              {/* Christmas Lights Border Effect */}
              <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-r from-red-500 via-yellow-400 via-green-500 via-red-500 to-yellow-400 animate-pulse"></div>
              <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-yellow-400 via-green-500 via-red-500 via-yellow-400 to-green-500 animate-pulse"></div>
              
              {/* Decorative Elements */}
              <div className="absolute top-0 left-0 w-full h-full opacity-20">
                <Snowflake className="absolute top-6 left-10 w-20 h-20 animate-float" />
                <Snowflake className="absolute bottom-6 right-10 w-20 h-20 animate-float-delayed" />
                <Snowflake className="absolute top-1/2 left-20 w-16 h-16 animate-float" />
                <Snowflake className="absolute top-1/2 right-20 w-16 h-16 animate-float-delayed" />
                <Star className="absolute top-12 right-20 w-14 h-14 animate-pulse" />
                <Star className="absolute bottom-12 left-20 w-14 h-14 animate-pulse-slow" />
                <Gift className="absolute top-20 left-1/4 w-12 h-12 animate-bounce-gentle" />
                <Gift className="absolute bottom-20 right-1/4 w-12 h-12 animate-bounce-gentle" />
              </div>
              
              <div className="relative z-10">
                {/* Animated Icon */}
                <div className="inline-flex items-center justify-center bg-yellow-400 text-gray-900 rounded-full p-6 mb-6 animate-bounce-gentle shadow-2xl border-4 border-yellow-300">
                  <Calendar className="w-12 h-12" />
                </div>
                
                <h4 className="text-4xl md:text-6xl lg:text-7xl font-black mb-4 drop-shadow-2xl">
                  🎄 Offers End December 31st! 🎅
                </h4>
                
                <p className="text-xl md:text-3xl mb-8 font-bold drop-shadow-lg">
                  Don't miss out on these exclusive Christmas savings!
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a href="https://thewindowexperts.ca/christmas-quote/" target="_blank" rel="noopener noreferrer">
                    <Button 
                      className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-10 py-7 text-xl md:text-2xl font-black rounded-2xl shadow-2xl border-4 border-yellow-300 transform hover:scale-110 transition-all"
                    >
                      <Gift className="inline w-7 h-7 mr-2" />
                      Claim Your Offer Now
                    </Button>
                  </a>
                  <a href="tel:4168440099">
                    <Button 
                      variant="outline"
                      className="bg-white/20 backdrop-blur-md border-4 border-white text-white hover:bg-white/30 px-10 py-7 text-xl md:text-2xl font-bold rounded-2xl shadow-xl"
                    >
                      📞 Call: (416) 844-0099
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Premium Windows & Doors
            </h3>
            <p className="text-xl text-gray-600">Energy-efficient, beautiful, and built to last</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="product-card group">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://thewindowexperts.ca/wp-content/uploads/2024/04/photo1712271976-jpeg.webp"
                  alt="Windows"
                  className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {e.target.src='https://via.placeholder.com/400x320/dc2626/ffffff?text=Windows'}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6 text-white">
                    <h4 className="text-2xl font-bold mb-2">Windows</h4>
                    <p className="text-sm">ENERGY STAR® rated for maximum efficiency</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="product-card group">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://thewindowexperts.ca/wp-content/uploads/2024/05/entrance-door.png"
                  alt="Entry Doors"
                  className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {e.target.src='https://via.placeholder.com/400x320/16a34a/ffffff?text=Entry+Doors'}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6 text-white">
                    <h4 className="text-2xl font-bold mb-2">Entry Doors</h4>
                    <p className="text-sm">Advanced security features included</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="product-card group">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://thewindowexperts.ca/wp-content/uploads/2024/04/photo1712271967-1-jpeg.webp"
                  alt="Patio Doors"
                  className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {e.target.src='https://via.placeholder.com/400x320/dc2626/ffffff?text=Patio+Doors'}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6 text-white">
                    <h4 className="text-2xl font-bold mb-2">Patio Doors</h4>
                    <p className="text-sm">Weather-sealed for Toronto winters</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section - Call to Action */}
      <section id="quote-form" className="py-20 bg-gradient-to-br from-red-900 via-red-800 to-green-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMTRjMCAxLjEtLjkgMi0yIDJzLTItLjktMi0yIC45LTIgMi0yIDIgLjkgMiAyIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>
        </div>
        
        {/* Floating Christmas Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          <Snowflake className="absolute top-10 left-[10%] w-20 h-20 text-white animate-float" />
          <Snowflake className="absolute top-[30%] right-[15%] w-16 h-16 text-white animate-float-delayed" />
          <Star className="absolute bottom-[20%] left-[20%] w-14 h-14 text-yellow-300 animate-pulse" />
          <Gift className="absolute bottom-[30%] right-[25%] w-16 h-16 text-white animate-bounce-gentle" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 px-8 py-4 rounded-full font-black mb-8 text-lg shadow-2xl border-4 border-yellow-300 animate-bounce-gentle">
              <Gift className="w-6 h-6" />
              GET YOUR FREE QUOTE
            </div>
            
            <h3 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 drop-shadow-2xl leading-tight">
              Ready to Transform Your Home?
            </h3>
            
            <p className="text-xl md:text-3xl text-white/90 font-bold mb-8 leading-relaxed">
              Fill out our quick form and get a personalized quote within 24 hours!
            </p>
            
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 border-4 border-white/20 mb-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="bg-yellow-400 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-gray-900" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">5 Exclusive Offers</h4>
                  <p className="text-white/80">Limited time Christmas deals</p>
                </div>
                <div className="text-center">
                  <div className="bg-yellow-400 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-gray-900" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">24-Hour Response</h4>
                  <p className="text-white/80">Quick personalized quote</p>
                </div>
                <div className="text-center">
                  <div className="bg-yellow-400 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Star className="w-8 h-8 text-gray-900 fill-current" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">100+ Reviews</h4>
                  <p className="text-white/80">Trusted by 2,000+ customers</p>
                </div>
              </div>
              
              <a href="https://thewindowexperts.ca/christmas-quote/" target="_blank" rel="noopener noreferrer" className="block">
                <Button 
                  className="w-full md:w-auto bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-16 py-8 text-2xl md:text-3xl font-black rounded-2xl shadow-2xl border-4 border-yellow-300 transform hover:scale-105 transition-all"
                >
                  <Gift className="inline w-8 h-8 mr-3" />
                  Get My FREE Quote Now
                </Button>
              </a>
              
              <p className="text-white/80 text-lg mt-6 font-semibold">
                🔒 Your information is confidential & secure
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <p className="text-white text-xl font-bold">Or call us directly:</p>
              <a href="tel:4168440099">
                <Button 
                  variant="outline"
                  className="bg-white/20 backdrop-blur-md border-2 border-white text-white hover:bg-white/30 px-8 py-4 text-xl font-bold rounded-xl"
                >
                  📞 (416) 844-0099
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h3>
            <div className="flex justify-center items-center gap-2 text-yellow-500">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-current" />
              ))}
              <span className="ml-2 text-gray-600 font-semibold">4.9/5 from 100+ reviews</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="testimonial-card hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">
                  "Igor and his team did a fantastic job throughout the entire process replacing our windows and front door. The installation team was hard working, polite, and precise."
                </p>
                <div className="font-semibold text-gray-900">- Adam Sweet</div>
              </CardContent>
            </Card>

            <Card className="testimonial-card hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">
                  "From the sales representative to the installation. Very professional. They will go the extra mile to make sure you're happy with the job. I would recommend Window Experts to family & friends."
                </p>
                <div className="font-semibold text-gray-900">- Chris</div>
              </CardContent>
            </Card>

            <Card className="testimonial-card hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">
                  "Love my windows! Workers were very nice, and they did an excellent job. Everything was completed on time. Will use them again!"
                </p>
                <div className="font-semibold text-gray-900">- Suraj Prasad</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_winter-savings/artifacts/ubx2lawl_image.png"
                alt="The Window Experts"
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-400 mb-4">
                Premium Windows and Doors Services for the Greater Toronto Area since 2017
              </p>
              <div className="flex gap-2 text-yellow-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <p className="text-sm text-gray-400 mt-1">2,000+ Happy Customers</p>
            </div>

            <div>
              <h5 className="text-lg font-bold mb-4">Contact Us</h5>
              <div className="space-y-2 text-gray-400">
                <p className="text-xl font-bold text-white">(416) 844-0099</p>
                <p>Serving Greater Toronto Area</p>
                <p>Available Mon-Sat, 8am-6pm</p>
              </div>
            </div>

            <div>
              <h5 className="text-lg font-bold mb-4">Why Choose Us</h5>
              <ul className="space-y-2 text-gray-400">
                <li>✓ Lifetime Warranty</li>
                <li>✓ Expert Installation</li>
                <li>✓ Energy Star Certified</li>
                <li>✓ 0% Financing Available</li>
                <li>✓ Free Estimates</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">🎄 Christmas Offers Valid Until December 31st, 2025</p>
            <p>© 2025 The Window Experts. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ChristmasLanding;